import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area,
  BarChart,
  Bar,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  ComposedChart
} from 'recharts';
import { TrendingUp, BarChart3, PieChart } from 'lucide-react';

const formatCurrency = (value) => {
  if (value === undefined || value === null) return 'R$ 0';
  if (value >= 1000000) {
    return `R$ ${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `R$ ${(value / 1000).toFixed(0)}K`;
  }
  return `R$ ${value.toFixed(0)}`;
};

const formatCurrencyFull = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p className="label font-semibold">Mês {label}</p>
        {payload.map((item, index) => (
          <div key={index} className="item">
            <span className="item-name" style={{ color: item.color }}>{item.name}:</span>
            <span className="item-value">{formatCurrencyFull(item.value)}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export const ProjectionCharts = ({ result }) => {
  const [activeChart, setActiveChart] = useState('evolucao');

  if (!result || !result.projecoes || result.projecoes.length === 0) {
    return null;
  }

  // Prepare data for charts (sample every nth point for better performance)
  const sampleRate = Math.max(1, Math.floor(result.projecoes.length / 20));
  const chartData = result.projecoes
    .filter((_, index) => index % sampleRate === 0 || index === result.projecoes.length - 1)
    .map((p) => ({
      mes: p.mes,
      data: p.data,
      valor_imovel: p.valor_imovel,
      total_investido: p.total_investido_corrigido,
      saldo_devedor: p.saldo_devedor,
      ganho_capital: p.ganho_capital_acumulado,
      ganho_real: p.ganho_real,
      lucro_liquido: p.lucro_liquido,
    }));

  const charts = [
    { id: 'evolucao', label: 'Evolução', icon: TrendingUp },
    { id: 'comparativo', label: 'Comparativo', icon: BarChart3 },
    { id: 'lucro', label: 'Lucro', icon: PieChart },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Chart Selector */}
      <div className="flex gap-2 flex-wrap">
        {charts.map((chart) => (
          <button
            key={chart.id}
            data-testid={`chart-tab-${chart.id}`}
            onClick={() => setActiveChart(chart.id)}
            className={`tab-item flex items-center gap-2 ${activeChart === chart.id ? 'active' : ''}`}
          >
            <chart.icon size={16} />
            {chart.label}
          </button>
        ))}
      </div>

      {/* Evolution Chart */}
      {activeChart === 'evolucao' && (
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-slate-800">
              Evolução do Investimento
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="chart-container" style={{ width: '100%', height: 320, minWidth: 1, minHeight: 1 }}>
              <ResponsiveContainer width="99%" height="100%" minWidth={1} minHeight={200} debounce={50}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorValorImovel" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#059669" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#059669" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorInvestido" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0891B2" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#0891B2" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                  <XAxis 
                    dataKey="mes" 
                    tick={{ fontSize: 12, fill: '#78716c' }}
                    tickFormatter={(value) => `${value}m`}
                  />
                  <YAxis 
                    tick={{ fontSize: 12, fill: '#78716c' }}
                    tickFormatter={formatCurrency}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend 
                    wrapperStyle={{ fontSize: '12px' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="valor_imovel"
                    name="Valor do Imóvel"
                    stroke="#059669"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorValorImovel)"
                  />
                  <Area
                    type="monotone"
                    dataKey="total_investido"
                    name="Total Investido"
                    stroke="#0891B2"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorInvestido)"
                  />
                  <Line
                    type="monotone"
                    dataKey="saldo_devedor"
                    name="Saldo Devedor"
                    stroke="#D97706"
                    strokeWidth={2}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Comparative Chart */}
      {activeChart === 'comparativo' && (
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-slate-800">
              Comparativo de Ganhos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="chart-container" style={{ width: '100%', height: 320, minWidth: 1, minHeight: 1 }}>
              <ResponsiveContainer width="99%" height="100%" minWidth={1} minHeight={200} debounce={50}>
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                  <XAxis 
                    dataKey="mes" 
                    tick={{ fontSize: 12, fill: '#78716c' }}
                    tickFormatter={(value) => `${value}m`}
                  />
                  <YAxis 
                    tick={{ fontSize: 12, fill: '#78716c' }}
                    tickFormatter={formatCurrency}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend 
                    wrapperStyle={{ fontSize: '12px' }}
                  />
                  <Bar
                    dataKey="ganho_capital"
                    name="Ganho de Capital"
                    fill="#059669"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="ganho_real"
                    name="Ganho Real"
                    fill="#0891B2"
                    radius={[4, 4, 0, 0]}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Profit Chart */}
      {activeChart === 'lucro' && (
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-slate-800">
              Projeção de Lucro Líquido
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="chart-container" style={{ width: '100%', height: 320, minWidth: 1, minHeight: 1 }}>
              <ResponsiveContainer width="99%" height="100%" minWidth={1} minHeight={200} debounce={50}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorLucro" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#059669" stopOpacity={0.4}/>
                      <stop offset="95%" stopColor="#059669" stopOpacity={0.05}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                  <XAxis 
                    dataKey="mes" 
                    tick={{ fontSize: 12, fill: '#78716c' }}
                    tickFormatter={(value) => `${value}m`}
                  />
                  <YAxis 
                    tick={{ fontSize: 12, fill: '#78716c' }}
                    tickFormatter={formatCurrency}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend 
                    wrapperStyle={{ fontSize: '12px' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="lucro_liquido"
                    name="Lucro Líquido"
                    stroke="#059669"
                    strokeWidth={3}
                    fillOpacity={1}
                    fill="url(#colorLucro)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProjectionCharts;
